import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatTime(minutes: number): string {
  if (minutes < 60) {
    return `${minutes} min`
  }
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`
}

export function calculateProgress(completed: number, total: number): number {
  return Math.round((completed / total) * 100)
}

export function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: number | undefined
  return (...args: Parameters<T>) => {
    clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait) as unknown as number
  }
}

export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args)
      inThrottle = true
      setTimeout(() => (inThrottle = false), limit)
    }
  }
}

export function validateEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

export function sanitizeInput(input: string): string {
  return input
    .replace(/[<>]/g, '')
    .trim()
    .slice(0, 1000) // Limit length
}

export function formatPercentage(value: number, decimals: number = 0): string {
  return `${value.toFixed(decimals)}%`
}

export function capitalizeFirst(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export function pluralize(count: number, singular: string, plural?: string): string {
  return count === 1 ? singular : (plural || `${singular}s`)
}

// Defensive date utilities to handle localStorage serialization
export function safeDate(dateOrString: Date | string | null | undefined): Date {
  if (!dateOrString) return new Date()
  return new Date(dateOrString)
}

export function safeGetTime(dateOrString: Date | string | null | undefined): number {
  return safeDate(dateOrString).getTime()
}

export function safeToLocaleDateString(dateOrString: Date | string | null | undefined, options?: Intl.DateTimeFormatOptions): string {
  return safeDate(dateOrString).toLocaleDateString(undefined, options)
}

export function safeToLocaleTimeString(dateOrString: Date | string | null | undefined, options?: Intl.DateTimeFormatOptions): string {
  return safeDate(dateOrString).toLocaleTimeString(undefined, options)
}

export function getTimeAgo(date: Date | string): string {
  const now = new Date()
  const dateObj = safeDate(date)
  const diffInSeconds = Math.floor((now.getTime() - dateObj.getTime()) / 1000)
  
  if (diffInSeconds < 60) return 'just now'
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} min ago`
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hr ago`
  if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} days ago`
  
  return dateObj.toLocaleDateString()
}

export function generateUserFriendlyId(prefix: string = ''): string {
  const adjectives = ['Smart', 'Quick', 'Bright', 'Swift', 'Clever', 'Sharp', 'Fast', 'Wise']
  const nouns = ['Learner', 'Explorer', 'Pioneer', 'Innovator', 'Creator', 'Builder', 'Maker', 'Solver']
  
  const adjective = adjectives[Math.floor(Math.random() * adjectives.length)]
  const noun = nouns[Math.floor(Math.random() * nouns.length)]
  const number = Math.floor(Math.random() * 999) + 1
  
  return `${prefix}${adjective}${noun}${number}`
}

export function analyzePromptQuality(prompt: string): number {
  let score = 0
  
  // Length check (optimal 10-200 characters)
  const length = prompt.length
  if (length >= 10 && length <= 200) score += 2
  else if (length > 200 && length <= 500) score += 1
  
  // Specificity indicators
  const specificityWords = ['specific', 'detailed', 'explain', 'analyze', 'compare', 'create', 'generate']
  if (specificityWords.some(word => prompt.toLowerCase().includes(word))) score += 2
  
  // Context provision
  if (prompt.includes('for') || prompt.includes('about') || prompt.includes('regarding')) score += 1
  
  // Clear instruction/question
  if (prompt.includes('?') || prompt.toLowerCase().startsWith('how') || 
      prompt.toLowerCase().startsWith('what') || prompt.toLowerCase().startsWith('why') ||
      prompt.toLowerCase().startsWith('create') || prompt.toLowerCase().startsWith('write')) score += 2
  
  // Politeness (optional but good practice)
  if (prompt.toLowerCase().includes('please') || prompt.toLowerCase().includes('help')) score += 1
  
  // Avoid too vague
  const vagueWords = ['something', 'anything', 'stuff', 'things']
  if (vagueWords.some(word => prompt.toLowerCase().includes(word))) score -= 1
  
  return Math.max(0, Math.min(10, score))
}

export function getRecommendationMatchScore(
  userProfile: {
    currentStage: number
    aiExperience: string
    businessType?: string
    problems: string[]
  },
  recommendation: {
    difficulty: string
    category: string
    requiredStage: number
  }
): number {
  let score = 0
  
  // Stage alignment (30 points max)
  if (userProfile.currentStage >= recommendation.requiredStage) {
    score += 30
  } else {
    score += Math.max(0, 30 - (recommendation.requiredStage - userProfile.currentStage) * 10)
  }
  
  // Experience alignment (25 points max)
  const experienceScores = {
    none: { beginner: 25, intermediate: 15, advanced: 5 },
    basic: { beginner: 20, intermediate: 25, advanced: 15 },
    intermediate: { beginner: 15, intermediate: 20, advanced: 25 },
    advanced: { beginner: 10, intermediate: 15, advanced: 25 }
  }
  
  score += experienceScores[userProfile.aiExperience as keyof typeof experienceScores]?.[recommendation.difficulty as keyof typeof experienceScores.none] || 0
  
  // Problem relevance (25 points max)
  const problemMatch = userProfile.problems.some(problem => 
    problem.toLowerCase().includes(recommendation.category.toLowerCase())
  )
  if (problemMatch) score += 25
  
  // Business type relevance (20 points max) - could be expanded
  score += 20 // Default for now
  
  return Math.min(100, score)
}